// Deep content for all pages: services, agents, industries, offshore.
// Persistent.com-inspired depth and structure.

export const STATS = [
  { value: '500+', label: 'Projects Delivered' },
  { value: '98%', label: 'Client Satisfaction' },
  { value: '60%', label: 'Faster Delivery' },
  { value: '40+', label: 'Countries Served' },
];

// ============================================================
// SERVICES (7) — each is a deep solution page
// ============================================================
export const SERVICES = [
  {
    id: 'erp-crm-agents',
    name: 'ERP & CRM Agents',
    shortName: 'ERP/CRM',
    tagline: 'Stop clicking. Start shipping.',
    icon: 'Buildings',
    summary: 'AI agents that live inside SAP, Salesforce, HubSpot, NetSuite, Zoho, and Dynamics — automating quote-to-cash, lead routing, invoice reconciliation, and forecasting.',
    hero_image_alt: 'Enterprise ERP dashboard with AI agents',
    overview: `Most enterprises lose 30-40% of their team's time to repetitive ERP/CRM workflows: re-keying data, chasing approvals, generating quotes, and reconciling invoices. Our ERP & CRM Agents plug directly into your existing stack and execute these workflows autonomously — with full audit trails, role-based access, and human-in-the-loop checkpoints where you need them.

XceedAgents builds custom AI agents tuned to your business rules, your data model, and your industry. Whether you're on SAP S/4HANA, Oracle NetSuite, Salesforce Sales Cloud, HubSpot, Zoho One, or Microsoft Dynamics 365, our agents extend your platform — they don't replace it.`,
    key_capabilities: [
      { title: 'Quote-to-Cash Automation', desc: 'Generate accurate quotes from RFPs, route approvals, push to ERP, and trigger invoicing — autonomously.' },
      { title: 'Invoice OCR & Reconciliation', desc: 'Match POs to invoices to GRNs with 99%+ accuracy. Auto-flag exceptions, route to humans only when needed.' },
      { title: 'Lead Scoring & Routing', desc: 'AI scoring against your historical wins, auto-assignment to reps, enrichment from public data sources.' },
      { title: 'Sales Forecasting', desc: 'Pipeline-weighted forecasting with deal-level confidence, deal-slip alerts, and quota tracking.' },
      { title: 'Master Data Stewardship', desc: 'Dedupe customers/products, enforce naming conventions, sync across systems with auditability.' },
      { title: 'Approval Workflows', desc: 'Multi-tier approvals routed via Slack/Teams/email with one-click approve and full context.' },
    ],
    integrations: ['SAP S/4HANA', 'Salesforce', 'HubSpot', 'NetSuite', 'Zoho CRM', 'Microsoft Dynamics 365', 'Oracle ERP Cloud', 'Workday'],
    industries: ['Manufacturing', 'Distribution', 'Financial Services', 'Healthcare', 'Retail', 'Logistics'],
    outcomes: [
      { metric: '70%', label: 'Reduction in manual data entry' },
      { metric: '4×', label: 'Faster quote-to-cash cycle' },
      { metric: '99.2%', label: 'Invoice match accuracy' },
      { metric: '$1.2M', label: 'Average annual savings (mid-market)' },
    ],
    process: [
      { step: 'Discovery', desc: 'We map your current ERP/CRM workflows, pain points, and ROI hotspots.' },
      { step: 'Agent Design', desc: 'We design agents around your business rules, role permissions, and data model.' },
      { step: 'Sandbox Build', desc: 'Build, test, and validate in your sandbox tenant with synthetic data.' },
      { step: 'Pilot Rollout', desc: 'Production pilot with 1-2 teams, full audit logging, fallback to humans.' },
      { step: 'Scale', desc: 'Roll out org-wide with training, change management, and quarterly tuning.' },
    ],
    faqs: [
      { q: 'Will this replace my Salesforce or SAP?', a: 'No. Our agents extend your existing systems via APIs. Your platforms remain the source of truth.' },
      { q: 'How do you handle compliance and audit trails?', a: 'Every agent action is logged with timestamp, inputs, decision rationale, and outcome. Fully SOC 2 / GDPR compatible.' },
      { q: 'What if the agent makes a mistake?', a: 'Human-in-the-loop checkpoints on all financial/legal actions. Confidence thresholds route low-confidence decisions to humans.' },
      { q: 'How long does deployment take?', a: 'Typical pilot: 4-6 weeks. Org-wide rollout: 3-4 months. We deliver value from week 2.' },
    ],
  },
  {
    id: 'whatsapp-bots',
    name: 'WhatsApp Bot Flows',
    shortName: 'WhatsApp',
    tagline: 'Your customers already live on WhatsApp.',
    icon: 'ChatCircle',
    summary: 'Conversational AI agents for ordering, support, appointments, payments, and lead nurture — powered by Meta Business Platform, Twilio, and Qikchat.',
    overview: `WhatsApp is the single most-used app in 60+ countries. Yet most businesses still send customers to slow websites and underused mobile apps. We change that.

XceedAgents builds production-grade WhatsApp agents that handle real business: take and confirm orders, accept payments via UPI/Stripe, book appointments synced to Google/Outlook calendars, answer support questions from your knowledge base, and nurture leads with personalized sequences. All on the platform your customers already use, 24/7, in any language.`,
    key_capabilities: [
      { title: 'Order Taking & Catalog Commerce', desc: 'Show menu/catalog inside WhatsApp, accept orders, collect payments, send confirmations.' },
      { title: 'Customer Support Agent', desc: 'Tier-1 support trained on your knowledge base. Escalates to humans with full context.' },
      { title: 'Appointment Booking', desc: 'Two-way sync with Google Calendar, Outlook, Calendly. SMS/WhatsApp reminders.' },
      { title: 'Payment Collection', desc: 'UPI, Stripe, Razorpay, PayPal — all inline in WhatsApp.' },
      { title: 'Lead Nurture Sequences', desc: 'Personalized drip campaigns with branching logic based on user replies.' },
      { title: 'Broadcast & Campaigns', desc: 'Targeted broadcasts respecting WhatsApp policy, with opt-in management.' },
    ],
    integrations: ['Meta WhatsApp Business API', 'Twilio', 'Qikchat', 'Stripe', 'Razorpay', 'Google Calendar', 'HubSpot', 'Shopify'],
    industries: ['Restaurants', 'E-commerce', 'Healthcare', 'Real Estate', 'Education', 'Beauty & Wellness'],
    outcomes: [
      { metric: '3×', label: 'Higher engagement vs email' },
      { metric: '24/7', label: 'Always-on support without staff' },
      { metric: '40%', label: 'Lower customer acquisition cost' },
      { metric: '85%', label: 'Open rate (vs 21% email)' },
    ],
    process: [
      { step: 'Use Case Mapping', desc: 'Identify the highest-ROI conversational flows for your business.' },
      { step: 'Conversation Design', desc: 'Design dialog trees, branching logic, fallback handling, brand voice.' },
      { step: 'Build & Train', desc: 'Build the agent, train on your data, integrate with your backend.' },
      { step: 'Compliance Review', desc: 'Meta Business verification, opt-in flows, GDPR/DPDP review.' },
      { step: 'Launch & Iterate', desc: 'Soft launch with A/B testing, analytics dashboard, weekly tuning.' },
    ],
    faqs: [
      { q: 'Do I need WhatsApp Business API?', a: "Yes for scale. We help you get Meta verified — typically 2-3 weeks. We can launch on personal/Business app for pilots." },
      { q: 'Can it speak my language?', a: 'Yes. Our agents support 50+ languages including all major Indian languages, Spanish, Arabic, French.' },
      { q: 'How do you handle escalation?', a: 'Confidence-based routing to live agents in your CRM (HubSpot, Salesforce, Zendesk).' },
      { q: 'Is this WhatsApp policy-compliant?', a: 'Yes. We design within Meta\'s 24-hour session window + approved template messages for proactive outreach.' },
    ],
  },
  {
    id: 'dashboard-analytics',
    name: 'Dashboard & Analytics Agents',
    shortName: 'Analytics',
    tagline: 'Dashboards that explain themselves.',
    icon: 'ChartLine',
    summary: 'Real-time KPI dashboards with AI narration, natural-language querying, and anomaly detection. Connect any data source.',
    overview: `Most dashboards are read-only walls of charts. People stare at them, can't tell what changed, and resort to asking analysts. We're done with that.

XceedAgents builds analytics agents that narrate your business every morning: what changed, why it changed, and what to do next. Stakeholders ask questions in plain English and get answers backed by your data. Anomalies surface automatically. Reports write themselves.`,
    key_capabilities: [
      { title: 'AI-Narrated Briefings', desc: 'Daily/weekly summaries of what moved and why — delivered to Slack, email, or WhatsApp.' },
      { title: 'Ask-Your-Data (NLQ)', desc: 'Natural language queries against your warehouse. "Why did churn spike last week?" → answered.' },
      { title: 'Anomaly Detection', desc: 'Automatic detection of statistical outliers across KPIs with root-cause hints.' },
      { title: 'Auto-Generated Reports', desc: 'Board decks, investor updates, weekly business reviews — generated on schedule.' },
      { title: 'Multi-Source Joins', desc: 'Unified view across Stripe, HubSpot, GA4, Shopify, your warehouse — without ETL nightmares.' },
      { title: 'Drill-Down Intelligence', desc: 'Click any number, get the cohort, the segments, the contributing factors.' },
    ],
    integrations: ['Snowflake', 'BigQuery', 'Postgres', 'Redshift', 'Databricks', 'GA4', 'Stripe', 'HubSpot', 'Shopify', 'Slack', 'Metabase'],
    industries: ['SaaS', 'Financial Services', 'E-commerce', 'Marketing', 'Operations', 'Healthcare'],
    outcomes: [
      { metric: '10 min', label: 'Morning briefing instead of 2 hours of meetings' },
      { metric: '5×', label: 'Faster anomaly detection' },
      { metric: '80%', label: 'Reduction in ad-hoc analyst requests' },
      { metric: '100%', label: 'Self-serve for non-technical teams' },
    ],
    process: [
      { step: 'KPI Workshop', desc: 'We align on the metrics that actually drive your business.' },
      { step: 'Data Inventory', desc: 'Catalog sources, define joins, design the semantic layer.' },
      { step: 'Agent Training', desc: 'Train the NLQ agent on your data model, business definitions, and historical Qs.' },
      { step: 'Dashboard Build', desc: 'Build the dashboard, narration engine, and anomaly detector.' },
      { step: 'Onboard & Tune', desc: 'Train your team, tune narration tone, quarterly model refresh.' },
    ],
    faqs: [
      { q: 'Do I need a data warehouse first?', a: 'Not necessarily. We can read from operational DBs for early stages and migrate to a warehouse later.' },
      { q: 'How accurate is the NLQ?', a: '90%+ on first try with our domain-tuned agents. Disambiguation prompts handle the rest.' },
      { q: 'Can it generate board decks?', a: 'Yes. PDF/PPTX/Notion export with charts, narration, and recommendations.' },
      { q: 'Where does my data live?', a: 'In your warehouse. We don\'t move or copy your data — agents query it in place.' },
    ],
  },
  {
    id: 'mobile-agents',
    name: 'Mobile App Agents',
    shortName: 'Mobile',
    tagline: 'Mobile apps that think for the user.',
    icon: 'DeviceMobile',
    summary: 'Native and cross-platform mobile apps with embedded AI agents — voice, vision, recommendations, smart automation.',
    overview: `A mobile app without intelligence is just a remote control for your backend. Modern users expect anticipation, personalization, and effortless workflows.

XceedAgents builds iOS and Android apps with AI agents embedded inside — agents that learn user preferences, anticipate intent, automate repetitive flows, and reduce taps by 40%+. From fintech to healthcare to retail, we ship apps that feel less like screens and more like personal assistants.`,
    key_capabilities: [
      { title: 'Voice Agents', desc: 'Always-on voice assistants tuned to your domain. Whisper, ElevenLabs, OpenAI Realtime.' },
      { title: 'Vision & OCR', desc: 'Scan documents, detect products, AR experiences — on-device or cloud.' },
      { title: 'Recommendation Engines', desc: 'Personalized feeds, product suggestions, content ranking based on behavior.' },
      { title: 'Smart Notifications', desc: 'Right message, right time. ML-driven send-time and content personalization.' },
      { title: 'Offline-First Sync', desc: 'Conflict-free sync, optimistic UI, and AI summarization of pending changes.' },
      { title: 'Native Performance', desc: 'Swift/Kotlin or React Native/Flutter — chosen for your latency and complexity needs.' },
    ],
    integrations: ['iOS (Swift, SwiftUI)', 'Android (Kotlin, Compose)', 'React Native', 'Flutter', 'Firebase', 'OneSignal', 'RevenueCat', 'Sentry'],
    industries: ['Fintech', 'Healthcare', 'Retail', 'Logistics', 'Consumer'],
    outcomes: [
      { metric: '60%', label: 'Faster ship-to-store' },
      { metric: '40%', label: 'Fewer taps with AI agents' },
      { metric: '4.8★', label: 'Average App Store rating' },
      { metric: '99.95%', label: 'Crash-free sessions' },
    ],
    process: [
      { step: 'UX Discovery', desc: 'User research, journey mapping, AI moment identification.' },
      { step: 'Prototype', desc: 'Interactive Figma prototype, on-device AI POC.' },
      { step: 'Sprint Build', desc: '2-week sprints, demo every Friday, TestFlight/Internal Testing.' },
      { step: 'Beta & Polish', desc: 'Beta with real users, performance/crash tuning, accessibility audit.' },
      { step: 'Launch & Grow', desc: 'App Store/Play optimization, post-launch analytics, growth experiments.' },
    ],
    faqs: [
      { q: 'Native or cross-platform?', a: 'We recommend based on your needs: native for AR/vision-heavy, RN/Flutter for time-to-market and 80% of CRUD apps.' },
      { q: 'Can AI run on-device?', a: 'Yes. CoreML, MLKit, ONNX Runtime — we run lightweight models locally for privacy and latency.' },
      { q: 'Do you handle App Store submission?', a: 'Yes. Including rejections, expedited reviews, ASO, and ongoing release management.' },
      { q: 'What about backend?', a: 'We build full-stack: backend, infra, and the app — or integrate with your existing API.' },
    ],
  },
  {
    id: 'product-development',
    name: 'New Product Development',
    shortName: 'Product',
    tagline: 'Idea on Monday. MVP shipped by month-end.',
    icon: 'Lightning',
    summary: 'From concept to production MVP in 4-6 weeks. AI-accelerated full-stack engineering by senior devs + 8 specialist AI agents.',
    overview: `Most product agencies promise 3-6 months and deliver in 9-12. We promise 4-6 weeks for an MVP and deliver in 4-6 weeks. Here's how: every senior engineer on our team is augmented by 8 specialized AI agents handling testing, code review, documentation, DevOps, UI polish, and more. The result is 60% faster delivery without quality compromise.

We've shipped 500+ products: B2B SaaS, marketplaces, fintech, healthtech, dev tools, internal platforms. We do the whole stack — architecture, design, frontend, backend, infrastructure, CI/CD, observability, App Store submissions, and post-launch growth.`,
    key_capabilities: [
      { title: 'MVP in 4-6 Weeks', desc: 'Production-grade MVP with auth, payments, analytics — not a throwaway prototype.' },
      { title: 'AI-Paired Design', desc: 'Figma + AI-generated component libraries, design tokens, accessibility-first.' },
      { title: 'Full-Stack Engineering', desc: 'React/Next.js, Node/Python/Go, Postgres/Mongo, AWS/GCP/Vercel — modern and battle-tested.' },
      { title: 'CI/CD & DevOps', desc: 'GitHub Actions, preview deploys, auto-scaling, observability from day one.' },
      { title: 'Analytics & PMF', desc: 'PostHog/Mixpanel from launch, user interview cadence, weekly cohort analysis.' },
      { title: 'Co-Build Mode', desc: 'Pair with your team — we move at your pace, document everything, hand off cleanly.' },
    ],
    integrations: ['React/Next.js', 'Node.js', 'Python (FastAPI/Django)', 'Go', 'Postgres', 'MongoDB', 'AWS', 'GCP', 'Vercel', 'Railway', 'Stripe', 'Auth0/Clerk'],
    industries: ['SaaS', 'Fintech', 'Healthtech', 'Marketplaces', 'Dev Tools', 'B2B Platforms'],
    outcomes: [
      { metric: '4-6 wk', label: 'MVP delivery' },
      { metric: '60%', label: 'Faster than traditional agencies' },
      { metric: '500+', label: 'Products shipped' },
      { metric: '98%', label: 'Client satisfaction' },
    ],
    process: [
      { step: 'Discovery Sprint', desc: '1-week deep dive: users, market, MVP scope, success metrics, tech architecture.' },
      { step: 'Design Sprint', desc: '1-2 weeks: clickable Figma prototype with full user flows.' },
      { step: 'Build Sprints', desc: '2-week sprints with Friday demos. Production deploys from week 1.' },
      { step: 'Launch', desc: 'App Store/web launch, analytics setup, support runbooks.' },
      { step: 'Grow', desc: 'Post-launch iteration, A/B tests, performance tuning, scaling.' },
    ],
    faqs: [
      { q: 'Do I own the code?', a: '100% yes. We commit to your repo from day one. You own all IP.' },
      { q: 'What if I need to extend after launch?', a: 'We offer retainer plans, dedicated team augmentation, or clean handoff to your in-house team.' },
      { q: 'How are estimates priced?', a: 'Fixed-price MVPs starting $25K. T&M for ongoing builds. ODC for long-term engagements.' },
      { q: 'Where is the team based?', a: 'Senior engineers in India (US/UK/EU timezone overlap). PM and architect available in your timezone.' },
    ],
  },
  {
    id: 'website-development',
    name: 'Website Development',
    shortName: 'Web',
    tagline: 'Websites that load instantly and convert harder.',
    icon: 'Globe',
    summary: 'Modern marketing sites and web apps built for speed, conversion, and SEO. Next.js, Tailwind, Vercel, headless CMS.',
    overview: `Your website is the front door of your company. If it loads slowly, looks dated, or doesn't convert — you're losing customers before they hear your pitch.

XceedAgents builds modern websites that hit 100/100 Lighthouse scores, rank top-3 on Google for your target keywords, and convert at 2-5× industry benchmarks. From corporate sites to high-converting landing pages to fully custom web apps, we ship sites that look beautiful and perform like infrastructure.`,
    key_capabilities: [
      { title: 'Next.js / React / Vue', desc: 'Modern frameworks for SEO + interactivity. Server components, edge rendering.' },
      { title: 'Headless CMS', desc: 'Sanity, Strapi, Contentful, Payload — non-technical teams ship content fast.' },
      { title: 'A/B Testing & CRO', desc: 'Conversion experiments from day one. Vercel/Cloudflare edge experimentation.' },
      { title: 'Core Web Vitals', desc: 'LCP <1s, CLS 0, INP <100ms. Lighthouse 95+ scores.' },
      { title: 'SEO Architecture', desc: 'Schema markup, sitemap automation, canonical strategy, internal linking.' },
      { title: 'Edge Hosting', desc: 'Vercel, Cloudflare Pages, Netlify — global CDN, zero-ops scaling.' },
    ],
    integrations: ['Next.js', 'React', 'Vue/Nuxt', 'Tailwind', 'Sanity', 'Strapi', 'Contentful', 'Vercel', 'Cloudflare', 'Algolia'],
    industries: ['SaaS', 'E-commerce', 'Media & Publishing', 'Agencies', 'Enterprise', 'Education'],
    outcomes: [
      { metric: '100/100', label: 'Lighthouse score' },
      { metric: '2.5×', label: 'Average conversion lift' },
      { metric: 'Top-3', label: 'SERP rankings for target keywords' },
      { metric: '<1s', label: 'Largest Contentful Paint' },
    ],
    process: [
      { step: 'Strategy', desc: 'Audience, IA, content map, conversion model, SEO keyword strategy.' },
      { step: 'Design', desc: 'Brand-aligned visual design, component library, prototyping.' },
      { step: 'Build', desc: 'Next.js build with headless CMS, edge hosting, analytics.' },
      { step: 'Launch', desc: 'SEO migration, redirects, Search Console setup, monitoring.' },
      { step: 'Grow', desc: 'A/B tests, monthly content velocity, rank tracking, CRO experiments.' },
    ],
    faqs: [
      { q: 'Can I edit content myself?', a: 'Yes. Headless CMS lets your marketing team publish without touching code.' },
      { q: 'What about my existing CMS?', a: 'We migrate from WordPress, Webflow, HubSpot, Squarespace — preserving SEO equity.' },
      { q: 'How do you measure success?', a: 'Pre/post Lighthouse scores, Core Web Vitals, organic traffic, conversion rate.' },
      { q: 'Do you handle ongoing maintenance?', a: 'Yes. Retainer plans for content velocity, A/B testing, ongoing SEO.' },
    ],
  },
  {
    id: 'ai-seo',
    name: 'AI SEO Agents',
    shortName: 'AI SEO',
    tagline: 'Rank higher without lifting a finger.',
    icon: 'MagnifyingGlass',
    summary: 'Programmatic SEO, AI content generation, technical audits, schema automation, and backlink intelligence — all autonomous.',
    overview: `SEO is increasingly a content-velocity game. The winners publish 100s of high-quality pages per month with technical precision. Doing that manually is impossible. Doing it with generic AI tools produces low-quality content that gets penalized.

XceedAgents builds custom SEO agents tuned to your domain, your brand voice, and your audience. They generate programmatic landing pages, write topic-cluster content, fix technical issues, build internal links, and monitor rankings — all autonomously. Your team approves. Google rewards.`,
    key_capabilities: [
      { title: 'Programmatic SEO', desc: 'Generate 100s-1000s of "location × service × industry" landing pages from data.' },
      { title: 'AI Content at Scale', desc: 'Topic-cluster blog content, comparison articles, glossary pages — tuned to brand voice.' },
      { title: 'Technical SEO Audits', desc: 'Continuous crawls, Core Web Vitals, broken links, redirects, indexation.' },
      { title: 'Schema Automation', desc: 'Auto-generate JSON-LD for every page type: Organization, Article, Product, FAQ, HowTo.' },
      { title: 'Internal Link Graph', desc: 'AI-optimized internal linking based on topic relevance and authority flow.' },
      { title: 'Backlink Intelligence', desc: 'Monitor competitor backlinks, identify outreach targets, track domain authority.' },
    ],
    integrations: ['Google Search Console', 'Ahrefs', 'Semrush', 'Screaming Frog', 'Algolia', 'Cloudflare', 'WordPress', 'Sanity', 'Webflow'],
    industries: ['SaaS', 'E-commerce', 'Marketplaces', 'Local Services', 'Media', 'Travel'],
    outcomes: [
      { metric: '1000+', label: 'Auto-generated SEO pages' },
      { metric: '3×', label: 'Organic traffic in 3-6 months' },
      { metric: '85%', label: 'Reduction in SEO content cost' },
      { metric: 'Top-3', label: 'For long-tail target keywords' },
    ],
    process: [
      { step: 'SEO Audit', desc: 'Technical audit, keyword gap analysis, competitor teardown.' },
      { step: 'Content Strategy', desc: 'Topic clusters, programmatic templates, editorial calendar.' },
      { step: 'Agent Setup', desc: 'Brand voice tuning, content templates, quality guardrails.' },
      { step: 'Launch & Monitor', desc: 'Roll out pages in batches, monitor indexation and rankings.' },
      { step: 'Iterate', desc: 'Weekly tuning based on Search Console signals and competitor moves.' },
    ],
    faqs: [
      { q: 'Will Google penalize AI content?', a: 'Google penalizes low-quality, scaled, manipulative content. Our agents produce expert-tuned, human-reviewed content that consistently ranks.' },
      { q: 'How fast do rankings move?', a: 'Long-tail: 4-8 weeks. Competitive: 3-6 months. We focus on quick wins first.' },
      { q: 'Do you handle the publishing?', a: 'Yes. We integrate with your CMS and publish automatically, or hand off drafts for review.' },
      { q: 'What about my brand voice?', a: 'Every agent is fine-tuned on your existing content. Voice consistency is part of QA.' },
    ],
  },
];

// ============================================================
// AI AGENTS (8) — internal team of specialist agents
// ============================================================
export const AI_AGENTS = [
  {
    id: 'dev-agent',
    name: 'Dev Agent',
    role: 'Full-Stack Engineering',
    icon: 'Code',
    color: '#EA580C',
    summary: 'Pairs with senior engineers to write production code 3-5× faster — across frontend, backend, infra, and tests.',
    capabilities: ['Frontend (React/Vue/Next)', 'Backend (Python/Node/Go)', 'Database design', 'API engineering', 'Refactoring & migrations', 'Code generation with full context'],
    integrations: ['GitHub', 'GitLab', 'VS Code', 'Cursor', 'Linear', 'Jira'],
    description: 'Our Dev Agent has the architectural context of your full repo. It writes, reviews, refactors, and ships code alongside senior engineers — never alone, always with human review.',
  },
  {
    id: 'test-agent',
    name: 'Test Agent',
    role: 'Quality Engineering',
    icon: 'CheckSquare',
    color: '#0D9488',
    summary: 'Autonomous test generation, coverage tracking, and regression detection. From unit to E2E.',
    capabilities: ['Unit test generation', 'Integration tests', 'E2E (Playwright/Cypress)', 'Coverage analysis', 'Mutation testing', 'Performance regression detection'],
    integrations: ['Playwright', 'Cypress', 'Jest', 'Vitest', 'Pytest', 'GitHub Actions'],
    description: 'Test Agent generates tests as code is written, identifies coverage gaps, and detects regressions before merge. 90%+ coverage maintained on every project.',
  },
  {
    id: 'review-agent',
    name: 'Review Agent',
    role: 'Code Review & Security',
    icon: 'ShieldCheck',
    color: '#EA580C',
    summary: 'Reviews every PR for bugs, security, performance, and style — before a human ever opens it.',
    capabilities: ['Bug detection', 'Security scanning (SAST)', 'Performance review', 'Style enforcement', 'Architectural drift detection', 'Dependency audits'],
    integrations: ['GitHub PRs', 'GitLab MRs', 'Bitbucket', 'Snyk', 'CodeQL', 'Semgrep'],
    description: 'Every pull request is reviewed by Review Agent first. It catches bugs, security issues, and style violations — leaving humans to focus on architecture and intent.',
  },
  {
    id: 'ui-agent',
    name: 'UI Agent',
    role: 'Design Systems & UX Polish',
    icon: 'Palette',
    color: '#0D9488',
    summary: 'Polishes interfaces to pixel perfection. Design tokens, component libraries, accessibility, motion.',
    capabilities: ['Design tokens', 'Component libraries (Storybook)', 'A11y (WCAG AA/AAA)', 'Motion design', 'Responsive QA', 'Dark mode'],
    integrations: ['Figma', 'Storybook', 'Tailwind', 'shadcn/ui', 'Radix', 'Framer Motion'],
    description: 'UI Agent ensures your product looks consistent, feels delightful, and works for every user — including those using assistive tech.',
  },
  {
    id: 'devops-agent',
    name: 'DevOps Agent',
    role: 'Infrastructure & Deployment',
    icon: 'CloudArrowUp',
    color: '#EA580C',
    summary: 'CI/CD pipelines, auto-scaling infra, observability, incident response — all autonomous.',
    capabilities: ['CI/CD pipelines', 'Kubernetes/ECS deployment', 'Auto-scaling rules', 'Observability (logs/traces/metrics)', 'Incident response runbooks', 'Cost optimization'],
    integrations: ['AWS', 'GCP', 'Vercel', 'Railway', 'Kubernetes', 'Datadog', 'Sentry', 'GitHub Actions'],
    description: 'DevOps Agent runs your infrastructure: zero-downtime deploys, auto-scaling, incident detection, cost tracking. Sleeps when you sleep, wakes when production cries.',
  },
  {
    id: 'docs-agent',
    name: 'Docs Agent',
    role: 'Documentation',
    icon: 'FileText',
    color: '#0D9488',
    summary: 'Auto-generates API docs, user guides, architecture diagrams, onboarding playbooks — kept fresh forever.',
    capabilities: ['API documentation (OpenAPI)', 'User guides & tutorials', 'Architecture diagrams', 'Runbooks', 'Changelogs', 'Onboarding playbooks'],
    integrations: ['Notion', 'Confluence', 'GitBook', 'Mintlify', 'ReadMe', 'Markdown'],
    description: 'Docs Agent keeps documentation perfectly in sync with code. Every API change, every new feature — instantly reflected in your docs.',
  },
  {
    id: 'support-agent',
    name: 'Support Agent',
    role: 'Customer Support',
    icon: 'Headphones',
    color: '#EA580C',
    summary: 'Tier-1 support agent trained on your product, docs, and ticket history. Escalates with full context.',
    capabilities: ['Knowledge-base answers', 'Ticket triage', 'Sentiment-based escalation', 'Multilingual (50+ langs)', 'Order/account lookups', 'Issue summarization'],
    integrations: ['Zendesk', 'Intercom', 'Freshdesk', 'HubSpot Service', 'Helpscout', 'Slack'],
    description: 'Support Agent handles 60-80% of tier-1 tickets without human intervention. The rest are triaged, summarized, and routed to the right human with full context.',
  },
  {
    id: 'data-agent',
    name: 'Data Agent',
    role: 'Analytics & Insights',
    icon: 'ChartBar',
    color: '#0D9488',
    summary: 'Autonomous analytics, anomaly detection, board-ready reports, and natural-language data exploration.',
    capabilities: ['Daily KPI digests', 'Anomaly detection', 'Cohort analysis', 'Auto-generated reports', 'NLQ over your warehouse', 'Forecasting'],
    integrations: ['Snowflake', 'BigQuery', 'Postgres', 'Redshift', 'Stripe', 'GA4', 'PostHog', 'Mixpanel'],
    description: 'Data Agent turns your warehouse into a conversation. Daily briefings, anomaly alerts, ad-hoc questions answered in plain English.',
  },
];

// ============================================================
// INDUSTRIES (8) — vertical solution pages
// ============================================================
export const INDUSTRIES = [
  {
    id: 'fintech',
    name: 'Financial Services',
    shortName: 'Fintech',
    icon: 'CurrencyDollar',
    headline: 'AI agents built for regulated finance.',
    summary: 'Trading platforms, KYC/AML automation, lending engines, payment systems, banking APIs, treasury management — built for compliance from day one.',
    challenges: ['Regulatory compliance (RBI, SEC, FINRA, MAS)', 'Real-time fraud detection', 'KYC/AML at scale', 'Legacy core banking integration', 'PCI DSS + SOC 2 + ISO 27001'],
    solutions: ['KYC/AML automation agents', 'Real-time fraud monitoring', 'Loan underwriting AI', 'Payments orchestration', 'Wealth-tech dashboards', 'Reconciliation automation'],
    cases: ['Neobank: 40% lower KYC cost', 'Lending platform: 3× faster decisions', 'Brokerage: 99.9% uptime trading engine'],
  },
  {
    id: 'healthcare',
    name: 'Healthcare & Life Sciences',
    shortName: 'Healthcare',
    icon: 'Heart',
    headline: 'HIPAA-grade AI for patient-centric care.',
    summary: 'EHR integration, telemedicine platforms, claims automation, clinical trial software, patient engagement agents — all HIPAA compliant.',
    challenges: ['HIPAA / HITECH compliance', 'EHR interop (HL7, FHIR)', 'Claims accuracy', 'Patient engagement', 'Clinical workflow automation'],
    solutions: ['Telemedicine platforms', 'Claims OCR & adjudication', 'Patient WhatsApp agents', 'Clinical trial portals', 'Provider dashboards', 'AI triage agents'],
    cases: ['Hospital chain: 50% faster claims', 'Telehealth: 4x patient retention', 'Clinical research: 3 weeks to onboard sites'],
  },
  {
    id: 'ecommerce',
    name: 'E-commerce & Retail',
    shortName: 'E-commerce',
    icon: 'ShoppingCart',
    headline: 'Commerce experiences that convert.',
    summary: 'D2C storefronts, marketplace platforms, OMS/WMS, AI recommendations, WhatsApp commerce, post-purchase automation.',
    challenges: ['Cart abandonment', 'Multi-channel inventory', 'Personalization at scale', 'Last-mile communication', 'Returns & RTO management'],
    solutions: ['Headless Shopify/Magento builds', 'WhatsApp commerce bots', 'Recommendation engines', 'OMS/WMS integration', 'Post-purchase flows', 'Returns automation'],
    cases: ['D2C brand: 2.5× conversion', 'Marketplace: 1M+ SKUs migrated', 'Quick commerce: 12-min delivery ops'],
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing & Industrial',
    shortName: 'Manufacturing',
    icon: 'Factory',
    headline: 'Industry 4.0 powered by AI.',
    summary: 'ERP modernization, IoT/SCADA integration, predictive maintenance, quality vision systems, supply chain visibility.',
    challenges: ['Legacy ERP/MES integration', 'Predictive maintenance', 'Quality control', 'Supply chain visibility', 'Labor productivity'],
    solutions: ['SAP/Oracle modernization', 'Computer vision QC', 'Predictive maintenance agents', 'Supply chain dashboards', 'Shop-floor mobile apps', 'IoT data pipelines'],
    cases: ['Auto OEM: 30% less downtime', 'Pharma: 99.7% QC accuracy', 'Steel: $4M annual savings'],
  },
  {
    id: 'saas',
    name: 'SaaS & B2B Platforms',
    shortName: 'SaaS',
    icon: 'Cloud',
    headline: 'Ship faster. Retain better. Scale smoother.',
    summary: 'Multi-tenant SaaS builds, billing & subscriptions, integration platforms, AI features for existing SaaS, admin portals.',
    challenges: ['Multi-tenancy & RBAC', 'Subscription billing', 'Customer onboarding', 'Integration sprawl', 'AI feature gap'],
    solutions: ['SaaS MVPs (4-6 weeks)', 'Stripe/billing integration', 'Admin & customer portals', 'Embedded AI features', 'Webhook & API platforms', 'Onboarding agents'],
    cases: ['HR-tech: $0→$1M ARR in 9 months', 'Sales SaaS: 4× activation', 'DevTool: 100K users on launch'],
  },
  {
    id: 'logistics',
    name: 'Logistics & Supply Chain',
    shortName: 'Logistics',
    icon: 'Truck',
    headline: 'Move smarter. Track everything.',
    summary: 'TMS/WMS modernization, fleet management, last-mile routing, carrier portals, real-time tracking agents.',
    challenges: ['Multi-carrier integration', 'Last-mile optimization', 'Real-time visibility', 'Driver app UX', 'Customer notifications'],
    solutions: ['TMS/WMS builds', 'Routing optimization AI', 'Driver/rider apps', 'Customer tracking pages', 'Carrier API hubs', 'WhatsApp delivery agents'],
    cases: ['3PL: 25% route savings', 'Quick-commerce: 8-min ETA accuracy', 'Trucking: 60% paperless'],
  },
  {
    id: 'education',
    name: 'Education & EdTech',
    shortName: 'EdTech',
    icon: 'GraduationCap',
    headline: 'Learning experiences that scale.',
    summary: 'LMS platforms, AI tutors, assessment engines, parent communication agents, student CRMs.',
    challenges: ['Engagement & retention', 'Personalized learning', 'Assessment at scale', 'Parent communication', 'B2B2C platform complexity'],
    solutions: ['LMS builds', 'AI tutoring agents', 'Assessment engines', 'Parent WhatsApp bots', 'Student CRMs', 'Live class platforms'],
    cases: ['K-12 LMS: 2M students', 'Test-prep: AI tutor 24/7', 'Coaching: 80% parent engagement'],
  },
  {
    id: 'real-estate',
    name: 'Real Estate & PropTech',
    shortName: 'Real Estate',
    icon: 'Building',
    headline: 'Property tech for modern buyers and renters.',
    summary: 'Listing platforms, CRM for brokers, virtual tours, lead-qualification agents, property management apps.',
    challenges: ['Lead overload', 'Slow response time', 'Listing quality', 'Multi-channel marketing', 'Virtual tours & content'],
    solutions: ['Listing platforms', 'Broker CRM', 'WhatsApp lead-qual agents', 'Virtual tour platforms', 'Property mgmt apps', 'Mortgage pre-qual'],
    cases: ['Brokerage: 5x lead response', 'Marketplace: 1M+ listings', 'PropMgmt: 70% paperless'],
  },
];

// ============================================================
// OFFSHORE OUTSOURCING — pillar pages for India outsourcing
// ============================================================
export const OFFSHORE_MODELS = [
  {
    id: 'offshore-development-center',
    name: 'Offshore Development Center (ODC)',
    tagline: 'Your own dedicated team in India.',
    summary: 'A dedicated, badged team working exclusively for you — managed by us, governed by you. Long-term, scalable, cost-efficient.',
    ideal_for: 'Companies needing 5-50+ engineers long-term; predictable monthly cost; full control over priorities',
    benefits: ['50-70% cost savings vs onshore', 'Dedicated team, no rotation', 'You set the roadmap', 'India + US timezone overlap', 'Quarterly business reviews', 'Direct collaboration with senior engineers'],
    timeline: '4-6 weeks to ramp first 5 engineers',
  },
  {
    id: 'staff-augmentation',
    name: 'Staff Augmentation',
    tagline: 'Senior engineers, plug-and-play.',
    summary: 'Senior engineers join your team for 3-18 months. They report to your tech leads, attend your standups, ship in your repo.',
    ideal_for: 'Filling specific skill gaps; scaling a delivery team; covering parental leave or attrition',
    benefits: ['Senior-only profiles (5+ yrs)', 'Vetted for English + culture fit', '2-week onboarding', '50%+ cost savings vs onshore', 'Replaceable on demand', 'Flexible duration'],
    timeline: '2-3 weeks from request to first day',
  },
  {
    id: 'project-outsourcing',
    name: 'Project Outsourcing (Fixed-Scope)',
    tagline: 'You define. We deliver. Fixed price.',
    summary: 'Defined scope, fixed price, fixed timeline. Best for greenfield builds, redesigns, and well-scoped projects.',
    ideal_for: 'Clear deliverables; budget certainty; one-off projects; vendor consolidation',
    benefits: ['Fixed price, no overruns', 'Milestone-based payments', 'Money-back guarantee on scope', 'AI-accelerated 60% faster', 'Dedicated PM', 'Free 2-week warranty post-delivery'],
    timeline: '4-12 weeks typical project length',
  },
  {
    id: 'managed-services',
    name: 'Managed Services',
    tagline: 'We run it. You sleep at night.',
    summary: 'Long-term ownership of platforms, apps, or workflows. SLA-backed, AI-monitored, fully managed.',
    ideal_for: 'Stable platforms needing ongoing care; legacy modernization; 24/7 ops; cost optimization',
    benefits: ['24/7 monitoring with AI', 'Defined SLAs', 'Predictable monthly cost', 'Continuous improvement', 'Compliance & security baked in', 'Quarterly cost reviews'],
    timeline: '4-6 weeks transition; permanent ownership thereafter',
  },
];

export const OFFSHORE_COMPARISON = {
  rows: [
    { feature: 'Hourly rate', us: '$30-60', traditional: '$25-80', onshore: '$120-250' },
    { feature: 'Senior engineer share', us: '100%', traditional: '20-40%', onshore: '60-80%' },
    { feature: 'AI agent augmentation', us: '8 specialist agents', traditional: 'None', onshore: 'Some' },
    { feature: 'Delivery speed', us: '60% faster', traditional: 'Baseline', onshore: 'Baseline' },
    { feature: 'Timezone overlap (US)', us: '4-5 hours', traditional: '2-3 hours', onshore: '8 hours' },
    { feature: 'English proficiency', us: 'Native/professional', traditional: 'Mixed', onshore: 'Native' },
    { feature: 'Code ownership', us: '100% client', traditional: '100% client', onshore: '100% client' },
    { feature: 'Retention guarantee', us: '12-month no-rotation', traditional: 'None', onshore: 'Variable' },
    { feature: 'Time to first engineer', us: '2-3 weeks', traditional: '4-8 weeks', onshore: '2-4 weeks' },
  ],
};

// ============================================================
// CLIENT LOGOS / TESTIMONIALS
// ============================================================
export const TESTIMONIALS = [
  {
    quote: "XceedAgents shipped our MVP in 5 weeks. Traditional vendors quoted 4 months. Their AI-augmented team is the real deal.",
    name: "Priya Mehta",
    role: "Founder & CEO",
    company: "Stellar Health",
  },
  {
    quote: "We replaced a 12-person team with XceedAgents' 4-engineer ODC. Output went up 30%, costs dropped 60%.",
    name: "Marcus Anderson",
    role: "VP Engineering",
    company: "Vantage Logistics",
  },
  {
    quote: "Their WhatsApp agent handles 70% of our customer queries 24/7. CSAT improved from 3.4 to 4.7.",
    name: "Rohan Kapoor",
    role: "Head of CX",
    company: "Bloom Direct",
  },
];

// ============================================================
// TECH STACK (used by Technology page)
// ============================================================
export const TECH_STACK = {
  frontend: [
    { id: 'react', name: 'React' },
    { id: 'nextjs', name: 'Next.js' },
    { id: 'vue', name: 'Vue.js' },
    { id: 'typescript', name: 'TypeScript' },
    { id: 'tailwind', name: 'Tailwind CSS' },
  ],
  backend: [
    { id: 'python', name: 'Python / FastAPI' },
    { id: 'nodejs', name: 'Node.js' },
    { id: 'go', name: 'Go' },
    { id: 'java', name: 'Java / Spring' },
  ],
  database: [
    { id: 'postgres', name: 'PostgreSQL' },
    { id: 'mongodb', name: 'MongoDB' },
    { id: 'redis', name: 'Redis' },
    { id: 'snowflake', name: 'Snowflake' },
    { id: 'bigquery', name: 'BigQuery' },
  ],
  ai: [
    { id: 'openai', name: 'OpenAI' },
    { id: 'anthropic', name: 'Anthropic Claude' },
    { id: 'gemini', name: 'Google Gemini' },
    { id: 'langchain', name: 'LangChain' },
    { id: 'vector', name: 'Vector DBs (Pinecone, Weaviate)' },
  ],
  cloud: [
    { id: 'aws', name: 'AWS' },
    { id: 'gcp', name: 'Google Cloud' },
    { id: 'azure', name: 'Azure' },
    { id: 'vercel', name: 'Vercel' },
  ],
  mobile: [
    { id: 'react-native', name: 'React Native' },
    { id: 'flutter', name: 'Flutter' },
    { id: 'swift', name: 'iOS Native (Swift)' },
    { id: 'kotlin', name: 'Android Native (Kotlin)' },
  ],
};

// ============================================================
// PRICING (engagement models)
// ============================================================
export const PRICING_MODELS = [
  {
    id: 'mvp-sprint',
    name: 'MVP Sprint',
    price: 'From $25,000',
    duration: '4-6 weeks',
    description: 'Fixed-scope, fixed-price MVP build.',
    features: ['Discovery + design + build', 'Production-grade MVP', 'Full code ownership', 'Hosting & CI/CD setup', '2-week warranty', 'Dedicated PM'],
    cta: 'Request for Proposal',
  },
  {
    id: 'odc',
    name: 'Offshore Development Center',
    price: 'From $8,500/mo per engineer',
    duration: '6+ months',
    description: 'Dedicated India team, fully managed.',
    features: ['Senior engineers only (5+ yrs)', '4-5h US timezone overlap', 'Dedicated, no rotation', 'AI agent augmentation', 'Quarterly business reviews', '12-month no-rotation guarantee'],
    cta: 'Start Hiring',
    featured: true,
  },
  {
    id: 'staff-aug',
    name: 'Staff Augmentation',
    price: 'From $45/hour',
    duration: '3-18 months',
    description: 'Senior engineers join your team.',
    features: ['Vetted senior engineers', '2-week onboarding', 'Reports to your team', 'Flexible duration', 'Replaceable on demand', 'India + US overlap'],
    cta: 'Hire an Engineer',
  },
  {
    id: 'managed',
    name: 'Managed Services',
    price: 'Custom',
    duration: 'Ongoing',
    description: 'Long-term ownership of platforms.',
    features: ['24/7 monitoring (AI)', 'SLA-backed', 'Predictable monthly cost', 'Continuous improvement', 'Compliance & security', 'Quarterly cost reviews'],
    cta: 'Talk to Sales',
  },
];
